import { ConfirmDialog, isolate } from '@batiflow/ui';
import { useTranslation } from 'react-i18next';
import { useNotify } from '@/shared/hooks/use-notify';
import { isStepUpRequired } from '../model';
import { useCancelApproval, useRefreshApprovals } from './approvals-api';
import {
  approvalErrorMessage,
  documentLabel,
  isApprovalClosed,
  type ApprovalFlow,
} from './approvals-model';

export interface CancelFlowDialogProps {
  flow: ApprovalFlow;
  onClose: () => void;
}

/**
 * "Cancel the approval" (the initiator or a manager of the folder), after a confirmation: the
 * reviewers can no longer decide and the document can receive a new version again. Decisions
 * already given stay in the history.
 */
export function CancelFlowDialog({ flow, onClose }: CancelFlowDialogProps) {
  const { t } = useTranslation('documents');
  const notify = useNotify();
  const cancel = useCancelApproval();
  const refresh = useRefreshApprovals(flow.spaceId);
  const label = isolate(documentLabel(flow.documentCode, flow.documentTitle));

  return (
    <ConfirmDialog
      open
      destructive
      title={t('approvals.cancel.title', { document: label })}
      body={t('approvals.cancel.body')}
      confirmLabel={t('approvals.cancel.confirm')}
      cancelLabel={t('approvals.cancel.keep')}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      onConfirm={async () => {
        await cancel.mutateAsync({ spaceId: flow.spaceId, flowId: flow.id });
        await refresh();
        notify({ intent: 'success', message: t('approvals.cancel.done', { document: label }) });
      }}
      onError={(error) => {
        if (isStepUpRequired(error)) return;
        notify({
          intent: isApprovalClosed(error) ? 'warning' : 'error',
          message: approvalErrorMessage(t, error),
        });
        if (isApprovalClosed(error)) {
          void refresh();
          onClose();
        }
      }}
    />
  );
}
